<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TelegramApiClient from 'dashboard/api/telegramApi';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const step = ref('credentials'); // credentials, qr, code, password, chats
const isLoading = ref(false);
const channelId = ref(null);
const authState = ref('initial');
const authError = ref('');
const qrLink = ref('');
let pollInterval = null;

// Auth method: 'phone' or 'qr'
const authMethod = ref('phone');

// Form fields
const phoneNumber = ref('');
const authCode = ref('');
const password = ref('');
const availableChats = ref([]);
const selectedChatIds = ref([]);

const isCredentialsValid = computed(() => {
  if (authMethod.value === 'qr') {
    return true;
  }
  return phoneNumber.value.length > 0;
});
const isCodeValid = computed(() => authCode.value.length >= 4);
const isPasswordValid = computed(() => password.value.length > 0);

const statusMessage = computed(() => {
  if (authError.value) return authError.value;
  const key = `INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.STATUS.${authState.value.toUpperCase()}`;
  return t(key);
});

function startPolling() {
  stopPolling();
  pollInterval = setInterval(async () => {
    if (!channelId.value) return;
    try {
      const { data } = await TelegramApiClient.getStatus(channelId.value);
      authState.value = data.auth_state;
      authError.value = data.auth_error_message || '';

      if (data.auth_state === 'awaiting_qr') {
        if (data.qr_link) {
          qrLink.value = data.qr_link;
        }
        if (step.value === 'credentials') {
          step.value = 'qr';
        }
      } else if (
        data.auth_state === 'awaiting_code' &&
        step.value === 'credentials'
      ) {
        step.value = 'code';
        stopPolling();
      } else if (data.auth_state === 'awaiting_password') {
        step.value = 'password';
        // Keep polling — daemon may transition to connected after password submit
      } else if (data.auth_state === 'connected') {
        step.value = 'chats';
        stopPolling();
        loadChats();
      } else if (data.auth_state === 'error') {
        stopPolling();
      }
    } catch {
      // Silently retry
    }
  }, 1500);
}

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

async function initiateAuth() {
  if (!isCredentialsValid.value) return;

  isLoading.value = true;
  try {
    if (authMethod.value === 'qr') {
      const { data } = await TelegramApiClient.requestQr({});
      channelId.value = data.channel_id;
      authState.value = data.auth_state;
      step.value = 'qr';
      startPolling();
    } else {
      const { data } = await TelegramApiClient.initiate({
        phone_number: phoneNumber.value,
      });
      channelId.value = data.channel_id;
      authState.value = data.auth_state;
      startPolling();
    }
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.API.ERROR_MESSAGE')
    );
  } finally {
    isLoading.value = false;
  }
}

async function submitCode() {
  if (!isCodeValid.value) return;

  isLoading.value = true;
  try {
    const { data } = await TelegramApiClient.verifyCode({
      channel_id: channelId.value,
      code: authCode.value,
    });
    authState.value = data.auth_state;

    if (data.auth_state === 'awaiting_password') {
      step.value = 'password';
    } else if (data.auth_state === 'connected') {
      step.value = 'chats';
      loadChats();
    } else {
      // Poll for state change
      startPolling();
    }
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.API.CODE_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
}

async function submitPassword() {
  if (!isPasswordValid.value) return;

  isLoading.value = true;
  try {
    const { data } = await TelegramApiClient.verifyPassword({
      channel_id: channelId.value,
      password: password.value,
    });
    authState.value = data.auth_state;

    if (data.auth_state === 'connected') {
      step.value = 'chats';
      loadChats();
    } else {
      startPolling();
    }
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.API.PASSWORD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
}

async function loadChats() {
  try {
    const { data } = await TelegramApiClient.getChats(channelId.value);
    availableChats.value = data.chats || [];
    selectedChatIds.value = data.monitored_chats || [];
  } catch {
    // Chats will be empty, user can proceed
  }
}

function toggleChat(chatId) {
  const index = selectedChatIds.value.indexOf(chatId);
  if (index > -1) {
    selectedChatIds.value.splice(index, 1);
  } else {
    selectedChatIds.value.push(chatId);
  }
}

async function createInbox() {
  isLoading.value = true;
  try {
    // Save selected chats
    if (selectedChatIds.value.length > 0) {
      await TelegramApiClient.selectChats({
        channel_id: channelId.value,
        chat_ids: selectedChatIds.value,
      });
    }

    // Fetch display name for inbox name
    const { data: statusData } = await TelegramApiClient.getStatus(
      channelId.value
    );
    const inboxName =
      statusData.display_name ||
      statusData.username ||
      `Telegram ${channelId.value}`;

    // Create inbox via standard endpoint
    const telegramApiChannel = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes`,
      {
        name: inboxName,
        channel: {
          type: 'telegram_api',
          channel_id: channelId.value,
        },
      }
    );

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: telegramApiChannel.data.id,
      },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.API.ERROR_MESSAGE')
    );
  } finally {
    isLoading.value = false;
  }
}

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.DESC')"
    />

    <!-- Step 1: Auth Method + Phone -->
    <form
      v-if="step === 'credentials'"
      class="flex flex-wrap flex-col mx-0 max-w-lg"
      @submit.prevent="initiateAuth"
    >
      <!-- Auth Method Selector -->
      <div class="flex-shrink-0 flex-grow-0 mb-4">
        <span class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.AUTH_METHOD.TITLE') }}
        </span>
        <div class="flex gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            :class="
              authMethod === 'phone'
                ? 'bg-woot-500 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
            "
            @click="authMethod = 'phone'"
          >
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.AUTH_METHOD.PHONE') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            :class="
              authMethod === 'qr'
                ? 'bg-woot-500 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
            "
            @click="authMethod = 'qr'"
          >
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.AUTH_METHOD.QR_CODE') }}
          </button>
        </div>
      </div>

      <!-- Phone Number (only for phone auth) -->
      <div v-if="authMethod === 'phone'" class="flex-shrink-0 flex-grow-0 mb-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="tel"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.PHONE_NUMBER.PLACEHOLDER'
              )
            "
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.PHONE_NUMBER.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isLoading"
          :disabled="!isCredentialsValid"
          type="submit"
          solid
          blue
          :label="
            authMethod === 'qr'
              ? $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.QR_CODE.SUBMIT_BUTTON')
              : $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.SUBMIT_BUTTON')
          "
        />
      </div>
    </form>

    <!-- Step QR: QR Code Confirmation -->
    <div v-if="step === 'qr'" class="flex flex-col mx-0 max-w-lg">
      <div
        class="mb-4 p-3 rounded-lg bg-woot-50 dark:bg-woot-800/20 text-woot-600 dark:text-woot-300"
      >
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.QR_CODE.SUBTITLE') }}
      </div>

      <h3 class="text-base font-medium mb-4">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.QR_CODE.TITLE') }}
      </h3>

      <div
        v-if="qrLink"
        class="flex flex-col items-center gap-4 p-6 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
      >
        <img
          :src="`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrLink)}`"
          alt="Telegram QR Code"
          class="w-[200px] h-[200px]"
        />
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.QR_CODE.LINK_LABEL') }}
        </p>
        <a
          :href="qrLink"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm text-woot-500 dark:text-woot-400 break-all hover:underline"
        >
          {{ qrLink }}
        </a>
      </div>

      <div
        v-else
        class="flex items-center justify-center p-6 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.QR_CODE.WAITING') }}
        </p>
      </div>

      <p class="mt-4 text-sm text-slate-500 dark:text-slate-400">
        {{ statusMessage }}
      </p>
    </div>

    <!-- Step 2: Verification Code -->
    <form
      v-if="step === 'code'"
      class="flex flex-wrap flex-col mx-0 max-w-lg"
      @submit.prevent="submitCode"
    >
      <div
        class="mb-4 p-3 rounded-lg bg-woot-50 dark:bg-woot-800/20 text-woot-600 dark:text-woot-300"
      >
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.AUTH_CODE.SUBTITLE') }}
      </div>

      <div class="flex-shrink-0 flex-grow-0 mb-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.AUTH_CODE.LABEL') }}
          <input
            v-model="authCode"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.AUTH_CODE.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isLoading"
          :disabled="!isCodeValid"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.VERIFY_BUTTON')"
        />
      </div>
    </form>

    <!-- Step 3: 2FA Password -->
    <form
      v-if="step === 'password'"
      class="flex flex-wrap flex-col mx-0 max-w-lg"
      @submit.prevent="submitPassword"
    >
      <div
        class="mb-4 p-3 rounded-lg bg-woot-50 dark:bg-woot-800/20 text-woot-600 dark:text-woot-300"
      >
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.PASSWORD.SUBTITLE') }}
      </div>

      <div class="flex-shrink-0 flex-grow-0 mb-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.PASSWORD.LABEL') }}
          <input
            v-model="password"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.PASSWORD.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isLoading"
          :disabled="!isPasswordValid"
          type="submit"
          solid
          blue
          :label="
            $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.VERIFY_PASSWORD_BUTTON')
          "
        />
      </div>
    </form>

    <!-- Step 4: Chat Selection -->
    <div v-if="step === 'chats'" class="flex flex-col mx-0 max-w-lg">
      <div
        class="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-800/20 text-green-600 dark:text-green-300"
      >
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.STATUS.CONNECTED') }}
      </div>

      <h3 class="text-base font-medium mb-2">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.CHAT_SELECTION.TITLE') }}
      </h3>
      <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.CHAT_SELECTION.SUBTITLE') }}
      </p>

      <div
        v-if="availableChats.length > 0"
        class="mb-4 max-h-60 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <label
          v-for="chat in availableChats"
          :key="chat.id"
          class="flex items-center gap-3 p-3 border-b border-slate-100 dark:border-slate-700 last:border-b-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <input
            type="checkbox"
            :checked="selectedChatIds.includes(chat.id)"
            class="form-checkbox"
            @change="toggleChat(chat.id)"
          />
          <span class="text-sm">{{ chat.title || chat.id }}</span>
          <span
            class="text-xs text-slate-400 dark:text-slate-500 ml-auto"
          >
            {{ chat.type }}
          </span>
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isLoading"
          type="button"
          solid
          blue
          :label="
            $t('INBOX_MGMT.ADD.TELEGRAM_API_CHANNEL.CREATE_INBOX_BUTTON')
          "
          @click="createInbox"
        />
      </div>
    </div>

    <!-- Status indicator -->
    <div
      v-if="authError"
      class="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-800/20 text-red-600 dark:text-red-300 max-w-lg"
    >
      {{ authError }}
    </div>
  </div>
</template>
